import { useCallback, useMemo, useState } from "react";
import { debounce } from "lodash";
import { useMutation } from "react-query";
import { t } from "@/i18n-js/instance";
import { emailValidationsApi } from "@/react/api/emailValidationApi";
import type { ApiError } from "@/react/config/CustomErrors";

interface EmailValidationResult {
  accept_as_signup: boolean;
  did_you_mean: string;
}

interface UseEmailValidationOptions {
  shouldUseDebounce?: boolean;
}

const localeScope = "authentication_app.signup";

export const useEmailValidation = (options: UseEmailValidationOptions = {}) => {
  const { shouldUseDebounce = true } = options;
  const [suggestedEmail, setSuggestedEmail] = useState<string | null>(null);
  const { mutateAsync, isLoading: isValidatingEmail } = useMutation<
    EmailValidationResult,
    ApiError,
    string
  >(email => emailValidationsApi.validateEmail(email));

  const shouldShowEmailValidation = window.RAILS_ENV === "production";

  const validateEmailWithApi = useCallback(
    async (email: string): Promise<true | string> => {
      if (!shouldShowEmailValidation) return true;

      try {
        const validationResult = await mutateAsync(email);
        if (!validationResult.accept_as_signup) {
          if (validationResult.did_you_mean) {
            setSuggestedEmail(validationResult.did_you_mean);
          }
          return t(
            `${localeScope}.form.errors.please_enter_valid_email_address`,
          );
        }
        setSuggestedEmail(null);
        return true;
      } catch (error) {
        console.error(error);
        setSuggestedEmail(null);
        return true;
      }
    },
    [mutateAsync, shouldShowEmailValidation],
  );

  const debouncedValidate = useMemo(
    () =>
      debounce((email: string, resolve: (value: true | string) => void) => {
        void validateEmailWithApi(email).then(resolve);
      }, 500),
    [validateEmailWithApi],
  );

  const validateEmail = useCallback(
    (email: string): Promise<true | string> => {
      if (!email) {
        setSuggestedEmail(null);
        return Promise.resolve(true);
      }

      if (!shouldUseDebounce) return validateEmailWithApi(email);

      return new Promise(resolve => {
        debouncedValidate(email, resolve);
      });
    },
    [debouncedValidate, validateEmailWithApi, shouldUseDebounce],
  );

  return {
    validateEmail,
    isValidatingEmail,
    suggestedEmail,
  };
};
